import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type RsvpQuestionWithAnswers,
  type RsvpQuestion,
  type AnswersByQuestion,
} from '../api/client';

export function useRsvpQuestions(eventId: string | undefined) {
  return useQuery<RsvpQuestionWithAnswers[]>({
    queryKey: ['rsvpQuestions', eventId],
    queryFn: () => api.rsvpQuestions.list(eventId!),
    enabled: !!eventId,
  });
}

export function useRsvpAnswers(eventId: string | undefined) {
  return useQuery<AnswersByQuestion[]>({
    queryKey: ['rsvpAnswers', eventId],
    queryFn: () => api.rsvpQuestions.getAnswers(eventId!),
    enabled: !!eventId,
  });
}

export function useCreateRsvpQuestion(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      question: string;
      type?: string;
      options?: string[];
      required?: boolean;
      sortOrder?: number;
    }) => api.rsvpQuestions.create(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rsvpQuestions', eventId] });
    },
  });
}

export function useUpdateRsvpQuestion(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionId,
      data,
    }: {
      questionId: string;
      data: {
        question?: string;
        type?: string;
        options?: string[] | null;
        required?: boolean;
        sortOrder?: number;
      };
    }) => api.rsvpQuestions.update(questionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rsvpQuestions', eventId] });
    },
  });
}

export function useDeleteRsvpQuestion(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) => api.rsvpQuestions.delete(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rsvpQuestions', eventId] });
    },
  });
}
